import { UiSchema } from '@rjsf/utils'

import {
    dimensionsMultipleKey,
    dimensionsMultipleKeyCheck,
    dimensionsSuffixKey,
    EAVE_GABLE_SOFFIT_TYPE_FOR_TG_WIDTH,
    EAVE_GABLE_SOFFIT_TYPE_VENT_TYPES,
    EAVE_GABLE_SOFFIT_TYPES_EXPOSED_SOFFIT,
    settingsFieldToDimensionsFieldMap,
} from '../../../constants/form-schema/eaveGableLength/constants'
import { otherValue } from '../../../constants/general-enums/value-other'

/**
 * Generate dimensions field
 *
 * @param dimensionsFields
 * @param uiOptions
 */
export const generateDimensionsFields = (dimensionsFields: string[], uiOptions?: UiSchema['ui:options']) => {
    return Object.assign(
        {},
        ...dimensionsFields.map((field) => ({
            [field]: {
                'ui:field': 'LumberDimensionsField',
                ...(uiOptions && { 'ui:options': uiOptions }),
            },
        }))
    )
}

/**
 * Return prepared field selected value or multiple for shared dimensions field between multiple selections
 *
 * @param fieldKey
 * @param fieldValue
 */
export const getSettingKeyValueForDimensionsField = (fieldKey: string, fieldValue?: string | null) => {
    const preparedFieldValue = fieldValue?.toLowerCase()
    const keyExists = fieldKey in dimensionsMultipleKeyCheck

    if (keyExists) {
        const fieldKeyValues = dimensionsMultipleKeyCheck[fieldKey]

        if (fieldKeyValues.includes(preparedFieldValue)) {
            return dimensionsMultipleKey
        }
    }

    // replace remove empty space and slashes
    return preparedFieldValue?.replaceAll(' ', '_')?.replaceAll('/', '')
}

/**
 * Transform dimensions settings when we receive them and when we submit
 * Receive example:
 *  fascia_1_dimensions -> fascia_1_<fascia_type_1_value>_dimensions
 *
 * Send example:
 *  fascia_1_<fascia_type_1_value>_dimensions -> fascia_1_dimensions
 *
 * @param settings
 * @param mode
 */
export const transformDimensionsSettings = (settings, mode: 'prepare' | 'revert') => {
    const updatedSettings = { ...settings }

    Object.entries(settingsFieldToDimensionsFieldMap).forEach(([fieldKey, dimensionKey]) => {
        if (!(fieldKey in settings)) return

        const settingsKeyValue = getSettingKeyValueForDimensionsField(fieldKey, settings[fieldKey])
        const dimensionsKeyPerFieldSelection = `${dimensionKey}_${settingsKeyValue}_${dimensionsSuffixKey}`
        const dimensionalKey = `${dimensionKey}_${dimensionsSuffixKey}`

        if (mode === 'prepare') {
            if (updatedSettings[dimensionsKeyPerFieldSelection]) {
                updatedSettings[dimensionalKey] = updatedSettings[dimensionsKeyPerFieldSelection]
            }
        } else if (mode === 'revert') {
            if (updatedSettings[dimensionalKey]) {
                updatedSettings[dimensionsKeyPerFieldSelection] = updatedSettings[dimensionalKey]
            }
        }

        const keyToDelete = mode === 'prepare' ? dimensionsKeyPerFieldSelection : dimensionalKey

        if (keyToDelete in updatedSettings) {
            delete updatedSettings[keyToDelete]
        }
    })

    return updatedSettings
}

/**
 * Clears soffit_type whenever it is no longer a valid selection for the current
 * exposed_soffit state.
 *
 * @param settings
 */
export const clearSoffitTypeForExposedSoffit = (settings) => {
    if (settings.exposed_soffit === true && !EAVE_GABLE_SOFFIT_TYPES_EXPOSED_SOFFIT.includes(settings?.soffit_type)) {
        delete settings.soffit_type
    }

    return settings
}

/**
 * Cleans up the `settings` object by nullifying or updating certain properties
 * based on specified conditions.
 *
 * @param {Object} settings - The settings object to be cleaned up.
 *
 * @returns {Object} - The cleaned-up settings object.
 */
export const eaveGableLengthFormSubmitCleanUp = (settings) => {
    if (!settings.robust_rafter_tail) {
        settings.rafter_tail_size = null
        settings.rafter_tail_length = null
        settings.rafter_tail_spacing = null
    }

    if (!settings.fascia_type) {
        settings.fascia_type = null
        settings.fascia_texture = null
        settings.fascia_width_1 = null
        settings.fascia_thickness_1 = null
    }

    if (!settings.fascia_type_2) {
        settings.fascia_type_2 = null
        settings.fascia_width_2 = null
        settings.fascia_thickness_2 = null
        settings.fascia_texture_2 = null
    }

    if (!settings.frieze_type) {
        settings.frieze_type = null
        settings.frieze_texture = null
        settings.frieze_width_1 = null
        settings.frieze_thickness_1 = null
    }

    if (!settings.frieze_type_2) {
        settings.frieze_type_2 = null
        settings.frieze_width_2 = null
        settings.frieze_thickness_2 = null
        settings.frieze_texture_2 = null
    }

    if (settings.width === otherValue) {
        settings.width = settings.width_other
    }

    settings.width_other = null

    settings.label = !settings.label && settings.label ? null : settings.label

    if (settings.exposed_soffit === true && !EAVE_GABLE_SOFFIT_TYPES_EXPOSED_SOFFIT.includes(settings?.soffit_type)) {
        settings.soffit_type = null
    }

    if (!EAVE_GABLE_SOFFIT_TYPE_FOR_TG_WIDTH.includes(settings?.soffit_type)) {
        settings.soffit_tg_width = null
    }

    if (!EAVE_GABLE_SOFFIT_TYPE_VENT_TYPES.includes(settings?.soffit_type)) {
        settings.soffit_vent_type = null
    }

    if (settings?.include_sub_fascia === false) {
        settings.sub_fascia_dimensions = null
    }

    // in case exposed_soffit is true and soffit type selection is different from dimensional lumber or panel (4x8)
    // we should clean other conditional fields in case we set them before switching exposed_soffit to true
    if (settings.exposed_soffit) {
        // soffit type selection which are available when exposed_soffit is ON or OFF
        const sharedSoffitTypes = ['dimensional lumber', 'panel (4x8)', 'sq ft']

        if (!sharedSoffitTypes?.includes(settings.soffit_type?.toLowerCase())) {
            const settingsToCleanUp = [
                'soffit_type',
                'soffit_vent_type',
                'panel_type',
                'plywood_profile',
                'texture',
                'soffit_texture',
                'soffit_multiple_dimensions',
                'soffit_lp_dimensions',
                'soffit_pvc_dimensions',
            ]

            settingsToCleanUp.forEach((setting) => (settings[setting] = null))
        }
    }

    return transformDimensionsSettings(settings, 'prepare')
}
